import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Mockups() {
  const [tab, setTab] = useState("mockups");
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");       // template key for batch
  const [previewProduct, setPreviewProduct] = useState("");
  const [tuning, setTuning] = useState(null);          // {key, box, shadow, scale}
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [job, setJob] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/admin/mockups/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/admin/mockups/status").then((r) => setJob(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (job?.status !== "running" && job?.status !== "queued") return;
    const t = setInterval(() => api.get("/admin/mockups/status").then((r) => setJob(r.data)), 3000);
    return () => clearInterval(t);
  }, [job?.status]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/products?search=${encodeURIComponent(q)}`).then((r) => setProducts(r.data.items || r.data || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const seed = async () => {
    const r = await api.post("/admin/mockups/templates/seed");
    toast.success(r.data.seeded ? `Seeded ${r.data.seeded} templates` : "Library already seeded");
    load();
  };

  const preview = async () => {
    if (!previewProduct || !tuning) return toast.error("Pick a product and a template");
    setPreviewing(true);
    try {
      const r = await api.post("/admin/mockups/preview", {
        product_id: previewProduct, template_key: tuning.key,
        box: tuning.box, shadow: tuning.shadow, scale: tuning.scale,
      });
      setPreviewUrl(r.data.url + `?t=${Date.now()}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setPreviewing(false); }
  };

  const runBatch = async () => {
    if (!selected) return toast.error("Pick a template");
    try {
      const est = await api.get("/admin/media/batch-estimate", { params: { kind: "mockups" } });
      if (est.data.exceeds_free && !window.confirm(
        `This batch could use ~${est.data.est_class_a_ops.toLocaleString()} Class A writes; only ${est.data.remaining_free_class_a.toLocaleString()} free writes remain this month. Batches skip work already done. Continue?`)) return;
      await api.post("/admin/mockups/run", { template_key: selected, product_ids: [], position: 1 });
      toast.success("Batch started — generated images land as the second gallery image");
      setTimeout(load, 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setT = (k, v) => setTuning((t) => ({ ...t, ...k }));
  const slider = (label, val, min, max, step, onChange, testid) => (
    <label className="block text-xs">
      <span className="flex justify-between"><span>{label}</span><span className="font-mono">{val}</span></span>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(Number(e.target.value))} className="w-full" data-testid={testid} />
    </label>
  );

  return (
    <div className="space-y-5" data-testid="mockups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Styled Mockup Generator</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Deterministic compositing — cut-out (rembg, cached once per source) onto scene templates with a tunable
            contact shadow. No per-image AI cost. Output lands as the second gallery image and is tagged as generated.
          </p>
        </div>
        {!templates.length && <Button onClick={seed} className="bg-stone-900 text-white" data-testid="mockup-seed-btn">Seed 5 scene templates</Button>}
      </div>

      <div className="flex gap-2">
        {[["mockups", "Styled mockups"], ["recolour", "Colour recolour"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border transition-colors ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-neutral-300 hover:border-stone-500"}`}
            data-testid={`mockups-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "recolour" && <RecolourPanel />}
      <div className={tab === "mockups" ? "contents" : "hidden"}>

      {job && job.status !== "idle" && (
        <p className="text-xs font-mono border border-neutral-200 bg-white px-3 py-2" data-testid="mockup-job-status">
          Last batch: {job.status}{job.total ? ` — ${job.done}/${job.total}` : ""}
          {job.status === "done" && job.results ? ` — ${job.results.filter((r) => r.status === "generated").length} generated, ${job.results.filter((r) => r.status === "skipped").length} skipped, ${job.results.filter((r) => r.status === "failed").length} failed` : ""}
        </p>
      )}

      <section className="bg-white border border-neutral-200 p-5" data-testid="mockup-templates">
        <h2 className="font-heading font-semibold mb-3">Scene templates</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {templates.map((t) => (
            <button key={t.id || t._id} onClick={() => { setSelected(t.id || t._id); setTuning({ key: t.id || t._id, box: { ...t.box }, shadow: { ...t.shadow }, scale: 1 }); setPreviewUrl(""); }}
              className={`border text-left ${selected === (t.id || t._id) ? "border-stone-900 ring-1 ring-stone-900" : "border-neutral-200"}`}
              data-testid={`template-${t.id || t._id}`}>
              <img src={t.plate_url} alt={t.name} className="w-full aspect-[4/5] object-cover" />
              <p className="text-[11px] p-2 leading-tight">{t.name}</p>
            </button>
          ))}
        </div>
      </section>

      {tuning && (
        <section className="bg-white border border-neutral-200 p-5 grid sm:grid-cols-2 gap-5" data-testid="mockup-tuning">
          <div className="space-y-3">
            <h2 className="font-heading font-semibold">Tune & preview</h2>
            <div>
              <Label>Product (primary white image is the source)</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or design number…" className="mb-1" data-testid="mockup-product-search" />
              <select value={previewProduct} onChange={(e) => setPreviewProduct(e.target.value)} className="w-full h-10 border border-neutral-200 rounded px-2 text-sm bg-white" data-testid="mockup-product-select">
                <option value="">Choose product…</option>
                {products.map((p) => <option key={p.id || p._id} value={p.id || p._id}>{p.design_number ? `${p.design_number} — ` : ""}{p.title}</option>)}
              </select>
            </div>
            {slider("Scale", tuning.scale, 0.5, 1.6, 0.05, (v) => setT({ scale: v }), "tune-scale")}
            {slider("Shadow offset X", tuning.shadow.offset_x, -60, 60, 2, (v) => setT({ shadow: { ...tuning.shadow, offset_x: v } }), "tune-shx")}
            {slider("Shadow offset Y", tuning.shadow.offset_y, 0, 100, 2, (v) => setT({ shadow: { ...tuning.shadow, offset_y: v } }), "tune-shy")}
            {slider("Shadow blur", tuning.shadow.blur, 4, 80, 2, (v) => setT({ shadow: { ...tuning.shadow, blur: v } }), "tune-blur")}
            {slider("Shadow opacity", tuning.shadow.opacity, 0.05, 0.9, 0.05, (v) => setT({ shadow: { ...tuning.shadow, opacity: v } }), "tune-opacity")}
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={preview} disabled={previewing} data-testid="mockup-preview-btn">
                {previewing ? "Compositing…" : "Preview"}
              </Button>
              <Button size="sm" variant="outline" onClick={async () => {
                await api.put(`/admin/mockups/templates/${tuning.key}`, { box: tuning.box, shadow: tuning.shadow });
                toast.success("Template saved");
              }} data-testid="mockup-save-template">Save as template default</Button>
            </div>
          </div>
          <div className="border border-neutral-100 bg-neutral-50 flex items-center justify-center min-h-64">
            {previewUrl
              ? <img src={previewUrl} alt="Mockup preview" className="max-h-96 object-contain" data-testid="mockup-preview-img" />
              : <p className="text-xs text-neutral-400 p-8 text-center">Preview renders here.<br />The shadow is what sells it — tune offset, blur and opacity until it sits on the surface.</p>}
          </div>
        </section>
      )}

      <section className="bg-white border border-neutral-200 p-5 flex items-center gap-3" data-testid="mockup-batch">
        <Sparkles size={16} className="text-gold" />
        <p className="text-sm flex-1">Run the selected template across <b>all products with a primary image</b>. Idempotent — products already having this template's mockup are skipped.</p>
        <Button onClick={runBatch} disabled={!selected || job?.status === "running"} className="bg-stone-900 text-white" data-testid="mockup-run-btn">
          Run batch (all products)
        </Button>
      </section>
      </div>
    </div>
  );
}

/* ---------------- Colour recolour (calibrated against real renders) ----------------
 * YELLOW GOLD source -> Rose + White derived sets. White/rhodium sources are
 * refused (<500 px above the core saturation cut — metal can't be isolated).
 * Targets are measured ABSOLUTE metal values; scaling is source-aware
 * (target/source, measured over core-mask pixels). Thresholds per target —
 * White needs 0.015/0.06 or the band reads warm in shadow gradients.
 * Baseline (yellow source, real renders): Rose 1.30/255 mean err, 1.12% px;
 * White 0.94/255, 0.18%. A tuning change that worsens these is a regression. */
function RecolourPanel() {
  const [cfg, setCfg] = useState(null);
  const [target, setTarget] = useState("Rose");
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [job, setJob] = useState(null);
  const [validation, setValidation] = useState(null);

  const load = () => {
    api.get("/admin/recolour/presets").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/recolour/status").then((r) => setJob(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(() => api.get("/admin/recolour/status").then((r) => setJob(r.data)), 3000);
    return () => clearInterval(t);
  }, [job?.status]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/products?search=${encodeURIComponent(q)}`).then((r) => setProducts(r.data.items || r.data || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (!cfg) return <p className="text-sm text-neutral-400 font-mono">LOADING…</p>;
  const preset = cfg.presets[target];

  const setP = (k, v) => setCfg({ ...cfg, presets: { ...cfg.presets, [target]: { ...preset, [k]: v } } });

  const preview = async () => {
    if (!productId) return toast.error("Pick a product first");
    setPreviewing(true);
    try {
      const r = await api.post("/admin/recolour/preview", { product_id: productId, target, preset });
      setPreviewUrl(r.data.url + `?t=${Date.now()}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setPreviewing(false); }
  };

  const save = async () => {
    try {
      await api.put("/admin/recolour/presets", { presets: cfg.presets });
      toast.success("Presets saved — derived images regenerate on the next batch (source+preset hash)");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const validate = async () => {
    if (!productId) return toast.error("Pick a product with real renders in two colours");
    setValidation(null);
    try {
      const r = await api.post("/admin/recolour/validate", { product_id: productId });
      setValidation(r.data);
      if (r.data.status === "scored") toast.success("Scored against real renders");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const run = async (force) => {
    try {
      const est = await api.get("/admin/media/batch-estimate", { params: { kind: "recolour" } });
      if (est.data.exceeds_free && !window.confirm(
        `This recolour batch could use ~${est.data.est_class_a_ops.toLocaleString()} Class A writes; only ${est.data.remaining_free_class_a.toLocaleString()} free writes remain this month. Unchanged assets are skipped. Continue?`)) return;
      await api.post("/admin/recolour/run", { product_ids: [], targets: cfg.derived_targets, force });
      toast.success("Recolour batch started — Rose + White derived from each yellow source");
      setTimeout(load, 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const slider = (label, val, min, max, step, onChange, testid) => (
    <label className="block text-xs" key={testid}>
      <span className="flex justify-between gap-2"><span>{label}</span><span className="font-mono">{val}</span></span>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(Number(e.target.value))} className="w-full" data-testid={testid} />
    </label>
  );

  return (
    <div className="space-y-5" data-testid="recolour-panel">
      <p className="text-xs border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="rc-source-note">
        Sources must be <b>yellow gold</b> renders (alpha is flattened onto white automatically). White/rhodium sources
        are refused — metal cannot be isolated. Baseline accuracy on the client's real renders: Rose mean error 1.30/255
        (1.12% px differ), White 0.94/255 (0.18%). A tuning change that worsens these is a regression — use Validate.
      </p>

      {job && job.status !== "idle" && (
        <p className="text-xs font-mono border border-neutral-200 bg-white px-3 py-2" data-testid="recolour-job-status">
          Batch: {job.status}{job.total ? ` — ${job.done}/${job.total}` : ""}
          {job.status === "done" && job.results ? ` — ${job.results.reduce((s, r) => s + (r.colours || []).filter((c) => c.status === "generated").length, 0)} generated, ${job.results.reduce((s, r) => s + (r.colours || []).filter((c) => c.status === "skipped").length, 0)} skipped, ${job.results.reduce((s, r) => s + (r.colours || []).filter((c) => c.status === "rejected_source" || c.status === "failed").length, 0)} rejected/failed` : ""}
        </p>
      )}

      <section className="bg-white border border-neutral-200 p-5 grid sm:grid-cols-2 gap-5">
        <div className="space-y-3">
          <h2 className="font-heading font-semibold">Presets & live preview</h2>
          <div className="flex gap-2">
            {(cfg.derived_targets || ["Rose", "White"]).map((t) => (
              <button key={t} onClick={() => { setTarget(t); setPreviewUrl(""); }}
                className={`px-3 py-1.5 text-xs border ${target === t ? "bg-stone-900 text-white border-stone-900" : "border-neutral-300"}`}
                data-testid={`recolour-target-${t.toLowerCase()}`}>{t === "White" ? "White / Rhodium" : `${t} Gold`}</button>
            ))}
          </div>
          {slider("Hue (°)", preset.hue, 0, 360, 0.1, (v) => setP("hue", v), "rc-hue")}
          {slider("Target saturation (absolute, metal px)", preset.sat, 0, 0.6, 0.001, (v) => setP("sat", v), "rc-sat")}
          {slider("Target value (absolute, metal px)", preset.val, 0.3, 1, 0.005, (v) => setP("val", v), "rc-val")}
          {slider("Mask: full metal above sat", preset.sat_hi, 0.01, 0.6, 0.005, (v) => setP("sat_hi", v), "rc-sat-hi")}
          {slider("Mask: untouched below sat (white keeps 0.015 to catch shadow tint)", preset.sat_lo, 0.005, 0.3, 0.005, (v) => setP("sat_lo", v), "rc-sat-lo")}
          <div>
            <Label>Product</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or design number…" className="mb-1" data-testid="rc-product-search" />
            <select value={productId} onChange={(e) => { setProductId(e.target.value); setValidation(null); }} className="w-full h-10 border border-neutral-200 rounded px-2 text-sm bg-white" data-testid="rc-product-select">
              <option value="">Choose product…</option>
              {products.map((p) => <option key={p.id || p._id} value={p.id || p._id}>{p.design_number ? `${p.design_number} — ` : ""}{p.title}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={preview} disabled={previewing} data-testid="rc-preview-btn">{previewing ? "Rendering…" : "Preview"}</Button>
            <Button size="sm" variant="outline" onClick={save} data-testid="rc-save-btn">Save presets</Button>
            <Button size="sm" variant="outline" onClick={validate} data-testid="rc-validate-btn">Validate vs real render</Button>
          </div>
          {validation?.scores && (
            <div className="border border-neutral-200 p-3 text-xs space-y-1" data-testid="rc-validation">
              <p className="font-mono uppercase text-[10px] text-neutral-500">Generated vs real render — {validation.design}</p>
              {Object.entries(validation.scores).map(([c, s]) => (
                <p key={c} className={s.regression ? "text-red-600" : "text-emerald-700"} data-testid={`rc-score-${c.toLowerCase()}`}>
                  {c}: mean error {s.mean_err}/255 (baseline {s.baseline.mean_err}) · {s.pct_diff}% pixels differ (baseline {s.baseline.pct_diff}%)
                  {s.regression ? " — REGRESSION, revisit presets" : " — within tolerance"}
                </p>
              ))}
            </div>
          )}
          {validation && !validation.scores && <p className="text-xs text-neutral-500">{validation.reason}</p>}
        </div>
        <div className="border border-neutral-100 bg-neutral-50 flex items-center justify-center min-h-64">
          {previewUrl ? <img src={previewUrl} alt="Recolour preview" className="max-h-96 object-contain" data-testid="rc-preview-img" />
            : <p className="text-xs text-neutral-400 p-8 text-center">Preview renders here.<br />Source-aware scaling: measured against the source's own metal saturation and value.</p>}
        </div>
      </section>

      <section className="bg-white border border-neutral-200 p-5 flex items-center gap-3" data-testid="recolour-batch">
        <p className="text-sm flex-1">Run across <b>all products with a yellow-gold source image</b> → Rose + White sets at serving sizes only. Idempotent per source+preset; real renders supersede generated; preset changes auto-regenerate.</p>
        <Button variant="outline" onClick={() => run(true)} disabled={job?.status === "running"} data-testid="rc-run-force">Regenerate all (after tuning)</Button>
        <Button onClick={() => run(false)} disabled={job?.status === "running"} className="bg-stone-900 text-white" data-testid="rc-run-btn">Run batch</Button>
      </section>
    </div>
  );
}
